import React from "react";
import { MdOutlineAttachment, MdClose, MdImage } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  getScenarioHelperContextPdfs,
  addScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  getScenarioHelperContextImages,
  addScenarioHelperContextImage,
  removeScenarioHelperContextImage,
} from "../state/chatbotSlice";
import { useAppDispatch } from "src/common/state/hooks";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import ModalGallery from "src/features/graph/components/ModalGallery";
import { IMedia } from "src/model/unityObject";
import { setMediaSearch } from "src/features/gallery/state/gallerySlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { Tooltip } from "@mui/material";

const PDF_MAX = 3;
const IMAGE_MAX = 1;

type ActiveTrack = "pdf" | "image" | null;

export const isMediaProcessing = (m: IMedia): boolean =>
  !!m.upload_status && m.upload_status !== "completed";

const PROCESSING_LABELS: Record<string, string> = {
  initialized: "En attente…",
  pending: "Upload…",
  transcoding: "Transcodage…",
  transcribing: "Transcription…",
};

const ProcessingBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600">
    <span className="inline-block w-2.5 h-2.5 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
    {PROCESSING_LABELS[status] || "Traitement…"}
  </span>
);

export interface AttachContextMediaProps {
  onMediaAdded?: (media: IMedia, track: "pdf" | "image") => void;
  isEditionEnabled: boolean;
}

const AttachContextMedia: React.FC<AttachContextMediaProps> = ({
  onMediaAdded,
  isEditionEnabled = true,
}) => {
  const dispatch = useAppDispatch();
  const selectedPdfs = useSelector(getScenarioHelperContextPdfs);
  const selectedImages = useSelector(getScenarioHelperContextImages);
  const [activeTrack, setActiveTrack] = React.useState<ActiveTrack>(null);

  const openPicker = (track: "pdf" | "image") => {
    if (track === "pdf" && selectedPdfs.length >= PDF_MAX) {
      dispatch(setAlert({ type: "warning", msg: "Maximum 3 PDF.", customTimeoutInMs: 3000 }));
      return;
    }
    if (track === "image" && selectedImages.length >= IMAGE_MAX) {
      dispatch(setAlert({ type: "warning", msg: "Maximum 1 image.", customTimeoutInMs: 3000 }));
      return;
    }
    setActiveTrack(track);
    // setMediaSearch merges via Object.assign (gallerySlice:1259), so we
    // explicitly clear media360tag — otherwise a value leaked from an
    // earlier picker context (e.g. a per-scene 360° picker) would silently
    // block selection of media whose tag doesn't match. Scenaria accepts
    // both 2D and 360° images as reference (same describeAttachedImages
    // path), so no tag filter is wanted here.
    dispatch(setMediaSearch({ mediaType: track, media360tag: "", query: "", page: 0 }));
    dispatch(openStandardModal("modal-gallery"));
  };

  const hasAttachments = selectedPdfs.length > 0 || selectedImages.length > 0;

  return (
    <div className="flex items-center gap-2 flex-wrap mb-1 max-h-20 overflow-y-auto">
      <Tooltip title="Joindre un PDF contextuel (max 3)">
        <span>
          <button
            onClick={() => openPicker("pdf")}
            disabled={!isEditionEnabled}
            className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 transition-colors"
          >
            <MdOutlineAttachment size={20} />
          </button>
        </span>
      </Tooltip>
      <Tooltip title="Joindre une image de référence (max 1)">
        <span>
          <button
            onClick={() => openPicker("image")}
            disabled={!isEditionEnabled}
            className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 transition-colors"
          >
            <MdImage size={20} />
          </button>
        </span>
      </Tooltip>

      {hasAttachments && (
        <>
          <div className="w-px h-5 bg-gray-200" />
          {selectedPdfs.map((pdf) => (
            <div
              key={pdf.id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-xs ${
                isMediaProcessing(pdf)
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-gray-100 border-gray-200 text-gray-600"
              }`}
            >
              <MdOutlineAttachment size={12} />
              <span className="max-w-[120px] truncate">{pdf.filename || pdf.name}</span>
              {isMediaProcessing(pdf) ? (
                <ProcessingBadge status={pdf.upload_status} />
              ) : isEditionEnabled ? (
                <button
                  onClick={() => dispatch(removeScenarioHelperContextPdf(pdf.id))}
                  className="text-gray-400 hover:text-red-500 ml-0.5"
                >
                  <MdClose size={12} />
                </button>
              ) : null}
            </div>
          ))}
          {selectedImages.map((img) => (
            <div
              key={img.id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-xs ${
                isMediaProcessing(img)
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-gray-100 border-gray-200 text-gray-600"
              }`}
            >
              <MdImage size={12} />
              <span className="max-w-[120px] truncate">{img.filename || img.name}</span>
              {isMediaProcessing(img) ? (
                <ProcessingBadge status={img.upload_status} />
              ) : isEditionEnabled ? (
                <button
                  onClick={() => dispatch(removeScenarioHelperContextImage(img.id))}
                  className="text-gray-400 hover:text-red-500 ml-0.5"
                >
                  <MdClose size={12} />
                </button>
              ) : null}
            </div>
          ))}
        </>
      )}

      <ModalGallery
        nodeId="profile"
        onMediaChange={(e: any) => {
          const newMedia = e as IMedia;
          if (activeTrack === "image") {
            dispatch(addScenarioHelperContextImage(newMedia));
            if (onMediaAdded) onMediaAdded(newMedia, "image");
          } else {
            dispatch(addScenarioHelperContextPdf(newMedia));
            if (onMediaAdded) onMediaAdded(newMedia, "pdf");
          }
          setActiveTrack(null);
          dispatch(closeStandardModal("modal-gallery"));
        }}
        allowedMediaTypeOverride={activeTrack === "image" ? "image" : "pdf"}
      />
    </div>
  );
};

export default AttachContextMedia;
